import { ReportTypeEnum } from '@revelio/data-access';

import type { GlobalAssistantReportCatalogEntry } from './types';

/**
 * Global AI assistant report catalog.
 *
 * Parameter semantics:
 * - isPrimary: true — anchor slot the report is built around (e.g. target geography, focus company).
 * - isPrimary: false with current/target naming — comparison side of a paired analysis.
 * - isPrimary: false with supportsMultiple — optional overflow filters (extra roles, skills, geographies).
 */

const GEOGRAPHY_CATEGORIES = [
  'region',
  'country',
  'metro_area',
  'msa',
  'state',
  'city',
] as const;

const ROLE_CATEGORIES = [
  'role_k50',
  'role_k150',
  'role_k500',
  'role_k1500',
] as const;

const COMPANY_CATEGORIES = ['company'] as const;

const SKILL_CATEGORIES = [
  'skill_k25',
  'skill_k50',
  'skill_k75',
  'skill_k150',
  'skill_k700',
  'skill_k1500',
  'skill_k2500',
  'skill_k5000',
  'skill_0',
  'skill_1',
  'skill_2',
  'skill_3',
] as const;

const SENIORITY_CATEGORIES = ['seniority'] as const;

export const GLOBAL_AI_ASSISTANT_REPORT_SELECTOR_CONFIDENCE_THRESHOLD = 70;

export const GLOBAL_ASSISTANT_REPORT_CATALOG: readonly GlobalAssistantReportCatalogEntry[] =
  [
    {
      reportType: ReportTypeEnum.JobComparison,
      enabled: true,
      reportName: 'Job Comparison',
      description: '15-page comparison of two positions',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_job_comparison',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          'User compares two jobs, roles, offers, companies, metros, or seniority levels for a candidate or recruiter.',
        whenNotToPick:
          'General market conditions (prefer Market Conditions) or sentiment-only questions.',
      },
      parameters: [
        {
          parameterId: 'current-role',
          displayName: 'Current Role',
          mwColumnLabels: ['Current Role'],
          expectedSelectionCategories: ROLE_CATEGORIES,
          isPrimary: true,
          resolverKind: 'role',
        },
        {
          parameterId: 'target-role',
          displayName: 'Target Role',
          mwColumnLabels: ['Target Role'],
          expectedSelectionCategories: ROLE_CATEGORIES,
          isPrimary: false,
          resolverKind: 'role',
        },
        {
          parameterId: 'current-metro',
          displayName: 'Current Metro Area',
          mwColumnLabels: ['Current Metro Area'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
        },
        {
          parameterId: 'target-metro',
          displayName: 'Target Metro Area',
          mwColumnLabels: ['Target Metro Area'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
        },
        {
          parameterId: 'current-company',
          displayName: 'Current Company',
          mwColumnLabels: ['Current Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
        {
          parameterId: 'target-company',
          displayName: 'Target Company',
          mwColumnLabels: ['Target Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'company',
        },
        {
          parameterId: 'current-seniority',
          displayName: 'Current Seniority',
          mwColumnLabels: ['Current Seniority'],
          expectedSelectionCategories: SENIORITY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'list',
          listFilterType: 'seniority',
        },
        {
          parameterId: 'target-seniority',
          displayName: 'Target Seniority',
          mwColumnLabels: ['Target Seniority'],
          expectedSelectionCategories: SENIORITY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'list',
          listFilterType: 'seniority',
        },
      ],
      projectionRules: [
        {
          kind: 'currentTargetPair',
          field: 'role',
          currentParameterId: 'current-role',
          targetParameterId: 'target-role',
        },
        {
          kind: 'currentTargetPair',
          field: 'geography',
          currentParameterId: 'current-metro',
          targetParameterId: 'target-metro',
        },
        {
          kind: 'currentTargetPair',
          field: 'company',
          currentParameterId: 'current-company',
          targetParameterId: 'target-company',
        },
        {
          kind: 'currentTargetPair',
          field: 'seniority',
          currentParameterId: 'current-seniority',
          targetParameterId: 'target-seniority',
        },
      ],
    },
    {
      reportType: ReportTypeEnum.MarketTightness,
      enabled: true,
      reportName: 'Market Conditions',
      description: '14-page overview of key factors to compare markets',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_market_tightness',
      launchTarget: 'promoted',
      templateId: 'report-company-market-conditions',
      selectorHints: {
        whenToPick:
          'User asks about labor market conditions, hiring difficulty, market tightness, or comparing geographies/roles for hiring.',
        whenNotToPick:
          'Job-to-job comparison for one candidate (prefer Job Comparison) or employee sentiment (prefer Sentiment).',
      },
      parameters: [
        {
          parameterId: 'target-geography',
          displayName: 'Target Geography',
          mwColumnLabels: ['Focus Geography', 'Target Geography'],
          promotedParameterIds: ['target-geography'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
        },
        {
          parameterId: 'comparison-geography',
          displayName: 'Comparison Geography',
          mwColumnLabels: [
            'Geography Comparison Group',
            'Comparison Geography',
          ],
          promotedParameterIds: ['comparison-geography'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
          supportsMultiple: true,
        },
        {
          parameterId: 'occupation',
          displayName: 'Occupation',
          mwColumnLabels: ['Occupation'],
          promotedParameterIds: ['occupation'],
          expectedSelectionCategories: ROLE_CATEGORIES,
          isPrimary: false,
          resolverKind: 'role',
          supportsMultiple: true,
        },
        {
          parameterId: 'skills',
          displayName: 'Skills',
          mwColumnLabels: ['Skills'],
          promotedParameterIds: ['skills'],
          expectedSelectionCategories: SKILL_CATEGORIES,
          isPrimary: false,
          resolverKind: 'skill',
          supportsMultiple: true,
        },
        {
          parameterId: 'seniority',
          displayName: 'Seniority',
          mwColumnLabels: ['Seniority'],
          promotedParameterIds: ['seniority'],
          expectedSelectionCategories: SENIORITY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'list',
          listFilterType: 'seniority',
          supportsMultiple: true,
        },
      ],
      projectionRules: [
        {
          kind: 'geographyPromptOrdered',
          primaryParameterId: 'target-geography',
          restParameterId: 'comparison-geography',
        },
        { kind: 'tdFieldAll', field: 'role', parameterId: 'occupation' },
        { kind: 'tdFieldAll', field: 'skill', parameterId: 'skills' },
        { kind: 'tdFieldAll', field: 'seniority', parameterId: 'seniority' },
      ],
    },
    {
      reportType: ReportTypeEnum.Sentiment,
      enabled: true,
      reportName: 'Sentiment',
      description: 'Company-level employee sentiment analysis',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_sentiment',
      launchTarget: 'promoted',
      templateId: 'report-company-sentiment',
      selectorHints: {
        whenToPick:
          'User asks about employee sentiment, culture, reviews, why people leave or stay at a company, or competitor sentiment comparisons.',
        whenNotToPick:
          'Prestige or education background analysis (prefer Prestige) or labor market tightness (prefer Market Conditions).',
      },
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          promotedParameterIds: ['622c111e-8d5d-426a-9c51-c4053a2b4c8d'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
        {
          parameterId: 'competitors',
          displayName: 'Competitors',
          mwColumnLabels: ['Comparison Company Group', 'Competitors'],
          promotedParameterIds: ['99a1ccb1-1940-4904-863a-21acb7aa2fb8'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'company',
          supportsMultiple: true,
        },
      ],
      projectionRules: [
        {
          kind: 'companyPrimaryRest',
          primaryParameterId: 'company',
          restParameterId: 'competitors',
        },
      ],
    },
    {
      reportType: ReportTypeEnum.Prestige,
      enabled: true,
      reportName: 'Prestige',
      description:
        '19-page analysis of employee education and corporate backgrounds',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_prestige',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          'User asks about company prestige scores, employer reputation rankings, or prestige benchmarks for a specific company (e.g. "prestige score for Amazon").',
        whenNotToPick:
          'General sentiment or culture questions (prefer Sentiment).',
      },
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
  ];
